import { useEffect, useRef } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import translations from '../../../i18n/translations';

const clients = [
  {
    name: 'HKRMA',
    fullName: '香港零售管理協會',
    logo: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/371464979f9b7d7adfc0fd83e2b8ee85.jpeg',
  },
  {
    name: '中國宏橋集團',
    fullName: '中國宏橋集團 (香港)',
    logo: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/cf2f0c48da8f604d9bf777742acf226e.jpeg',
  },
  {
    name: '海城裝飾工程',
    fullName: '海城裝飾工程有限公司',
    logo: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/85b083e1abc8076ec87bb2396d9ffe7c.png',
  },
  {
    name: 'AIM Limited',
    fullName: 'Advanced Intelligence Management Limited',
    logo: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/80a18eaaa79af63f7eab25969fd50d6d.png',
  },
  {
    name: 'Festive Decor',
    fullName: 'Festive Decor Commercial Display Limited',
    logo: 'https://static.readdy.ai/image/48dde99e8fd8a26df059daa5a847ba45/c3fb8c1baa17a60f02813d1a42c4daf1.png',
  },
];

function LogoCard({ name, fullName, logo, icon }: { name: string; fullName: string; logo?: string | null; icon?: string }) {
  return (
    <div className="flex-shrink-0 flex flex-col items-center gap-4 px-10 py-8 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-300 cursor-pointer mx-4 min-w-[240px]">
      <div className="w-28 h-28 flex items-center justify-center rounded-lg bg-gray-50 overflow-hidden flex-shrink-0">
        {logo ? (
          <img src={logo} alt={name} className="w-full h-full object-contain" />
        ) : (
          <i className={`${icon} text-4xl text-gray-600`}></i>
        )}
      </div>
      <span className="text-base font-bold text-gray-900 text-center whitespace-nowrap">{fullName}</span>
    </div>
  );
}

export default function ClientLogos() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(0);
  const scrollPosRef = useRef<number>(0);
  const isPausedRef = useRef<boolean>(false);
  const { language } = useLanguage();
  const t = translations[language].clientLogos;

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const speed = 0.4;

    const animate = () => {
      if (!isPausedRef.current) {
        scrollPosRef.current += speed;
        const halfWidth = container.scrollWidth / 2;
        if (scrollPosRef.current >= halfWidth) {
          scrollPosRef.current = 0;
        }
        container.style.transform = `translateX(-${scrollPosRef.current}px)`;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    const handleMouseEnter = () => { isPausedRef.current = true; };
    const handleMouseLeave = () => { isPausedRef.current = false; };

    container.addEventListener('mouseenter', handleMouseEnter);
    container.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationRef.current);
      container.removeEventListener('mouseenter', handleMouseEnter);
      container.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <section className="py-16 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <div className="text-center">
          <div className="inline-block px-4 py-1.5 bg-white text-[#00A3FF] text-xs font-semibold tracking-wider rounded-full mb-4 shadow-sm">
            TRUSTED CLIENTS
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-3">{t.title}</h2>
          <p className="text-base text-gray-500">{t.subtitle}</p>
        </div>
      </div>

      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none"></div>
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none"></div>

        <div className="overflow-hidden">
          <div ref={scrollRef} className="flex will-change-transform">
            {[...clients, ...clients, ...clients].map((client, index) => (
              <LogoCard
                key={`${client.name}-${index}`}
                name={client.name}
                fullName={client.fullName}
                logo={client.logo}
                icon={client.icon}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}